import Image from "next/image";
import Link from "next/link";
import React from "react";

export interface DoctorProfileSectionProps {
  heading?: string;
  doctorName?: string;
  qualifications?: string;
  philosophyQuote?: string;
  bio?: string;
  profileCtaLabel?: string;
  profileCtaLink?: string;
  imageUrl?: string;
  imageAlt?: string;
}

/**
 * Doctor Profile Section - Dr. John Watts
 * High trust zone with doctor photo and philosophy
 */
export default function DoctorProfileSection({
  heading = "Meet Our Doctor",
  doctorName = "Dr. John Watts",
  qualifications = "MBBS, MD",
  philosophyQuote = "Not every patient needs surgery. Every patient deserves an honest medical assessment.",
  bio = "Dr. John Watts leads Trichos with a focus on ethical practice, personalised planning, and medically sound outcomes.",
  profileCtaLabel = "Meet the Doctor",
  profileCtaLink = "/dermatologist/dr-john-watts",
  imageUrl,
  imageAlt = "Doctor profile",
}: DoctorProfileSectionProps) {
  return (
    <section className="py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid gap-8 md:grid-cols-2 md:items-center md:gap-12">
          {/* Left: Doctor Photo */}
          <div className="relative mx-auto w-full max-w-xs md:max-w-sm">
            <div
              className="relative w-full overflow-hidden rounded-3xl bg-gray-100 shadow-lg"
              style={{ aspectRatio: "3/4" }}
            >
              {imageUrl ? (
                <Image
                  src={imageUrl}
                  alt={imageAlt || doctorName}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  unoptimized
                  priority={false}
                />
              ) : (
                <div className="flex h-full items-center justify-center bg-gradient-to-br from-violet-50 to-violet-100">
                  <div className="text-center">
                    <div className="mb-4 text-6xl">👨‍⚕️</div>
                    <p className="text-sm text-gray-600">{doctorName}</p>
                    <p className="mt-2 text-xs text-gray-500">
                      (Replace with actual photo)
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: Doctor Info */}
          <div>
            <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">
              {heading}
            </h2>

            <h3 className="mb-2 text-2xl font-bold text-violet-950">
              {doctorName}
            </h3>

            <p className="mb-4 text-lg font-semibold text-gray-700">
              {qualifications}
            </p>

            <blockquote className="mb-6 border-l-4 border-violet-950 pl-4 italic text-gray-700">
              “{philosophyQuote}”
            </blockquote>

            <p className="mb-8 text-base text-gray-600">{bio}</p>

            {profileCtaLabel && profileCtaLink && (
              <Link
                href={profileCtaLink}
                className="inline-block rounded-full bg-black px-4 py-2 text-sm font-medium transition-colors hover:bg-gray-800 md:text-base"
                style={{ color: "#F7E9AE" }}
              >
                {profileCtaLabel}
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
